import { useRef, useState } from 'react';
import { api, camposDoErro, mensagemDoErro, type Template } from '../api';
import { useApp } from '../estado';
import { useRecurso } from '../ganchos';
import {
  formatarDataHora,
  preencherModelo,
  variaveisDoCorpo,
  variaveisExemplo,
} from '../formatacao';
import {
  Aviso,
  Campo,
  Carregando,
  EstadoVazio,
  Modal,
  TituloPagina,
} from '../componentes/ui';

const VARIAVEIS: Array<{ chave: string; descricao: string }> = [
  { chave: 'primeiro_nome', descricao: 'Primeiro nome do Titular' },
  { chave: 'nome', descricao: 'Nome completo' },
  { chave: 'valor', descricao: 'Valor da fatura' },
  { chave: 'vencimento', descricao: 'Data de vencimento' },
  { chave: 'contrato', descricao: 'Número do contrato' },
  { chave: 'fatura', descricao: 'Identificador da fatura' },
  { chave: 'empresa', descricao: 'Nome de exibição do Provedor' },
  { chave: 'dias_desbloqueio', descricao: 'Dias de liberação concedidos' },
  { chave: 'data_liberacao', descricao: 'Data prevista de liberação' },
];

interface Formulario {
  nome: string;
  chave: string;
  corpo: string;
  metaTemplateId: string;
}

const FORMULARIO_VAZIO: Formulario = {
  nome: '',
  chave: '',
  corpo:
    'Olá, {{primeiro_nome}}! Sua fatura de {{valor}} vence em {{vencimento}}.\n\nPague pelo aplicativo do {{empresa}}.',
  metaTemplateId: '',
};

type EstadoModal =
  | { modo: 'criar' }
  | { modo: 'editar'; template: Template }
  | null;

export function Templates() {
  const { provedor, notificar } = useApp();
  const templates = useRecurso(() => api.templates(), []);
  const [modal, setModal] = useState<EstadoModal>(null);
  const [formulario, setFormulario] = useState<Formulario>(FORMULARIO_VAZIO);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [salvando, setSalvando] = useState(false);
  const [removendo, setRemovendo] = useState<Template | null>(null);
  const [removendoAgora, setRemovendoAgora] = useState(false);
  const areaCorpo = useRef<HTMLTextAreaElement>(null);

  const empresa = provedor?.branding.nomeExibicao ?? 'seu Provedor';
  const exemplo = variaveisExemplo(empresa);
  const fuso = provedor?.fuso ?? 'America/Sao_Paulo';
  const lista = templates.dados?.templates ?? [];

  const abrirCriacao = () => {
    setFormulario(FORMULARIO_VAZIO);
    setErros({});
    setModal({ modo: 'criar' });
  };

  const abrirEdicao = (template: Template) => {
    setFormulario({
      nome: template.nome,
      chave: template.chave,
      corpo: template.corpo,
      metaTemplateId: template.metaTemplateId === null ? '' : String(template.metaTemplateId),
    });
    setErros({});
    setModal({ modo: 'editar', template });
  };

  const fecharModal = () => {
    if (salvando) return;
    setModal(null);
  };

  const inserirVariavel = (chave: string) => {
    const trecho = `{{${chave}}}`;
    const area = areaCorpo.current;
    const corpo = formulario.corpo;
    const inicio = area?.selectionStart ?? corpo.length;
    const fim = area?.selectionEnd ?? corpo.length;
    const novo = corpo.slice(0, inicio) + trecho + corpo.slice(fim);
    setFormulario((atual) => ({ ...atual, corpo: novo }));
    if (area) {
      window.requestAnimationFrame(() => {
        area.focus();
        area.setSelectionRange(inicio + trecho.length, inicio + trecho.length);
      });
    }
  };

  const salvar = async () => {
    const locais: Record<string, string> = {};
    if (formulario.nome.trim().length < 2) locais.nome = 'Dê um nome com pelo menos 2 letras.';
    if (!/^[a-z0-9_]{2,60}$/.test(formulario.chave)) {
      locais.chave = 'Use minúsculas, números e _ (ex.: cobranca_padrao).';
    }
    if (formulario.corpo.trim().length < 5) locais.corpo = 'Escreva a mensagem que o Titular vai ler.';
    if (formulario.metaTemplateId !== '' && !/^\d+$/.test(formulario.metaTemplateId)) {
      locais.metaTemplateId = 'O ID da Meta é numérico.';
    }
    if (Object.keys(locais).length > 0) {
      setErros(locais);
      return;
    }

    const metaTemplateId =
      formulario.metaTemplateId === '' ? null : Number(formulario.metaTemplateId);

    setSalvando(true);
    try {
      if (modal?.modo === 'editar') {
        await api.atualizarTemplate(modal.template.id, {
          nome: formulario.nome.trim(),
          corpo: formulario.corpo,
          metaTemplateId,
        });
        notificar('sucesso', 'Template atualizado.');
      } else {
        await api.criarTemplate({
          chave: formulario.chave.trim(),
          nome: formulario.nome.trim(),
          corpo: formulario.corpo,
          metaTemplateId,
        });
        notificar('sucesso', 'Template criado.');
      }
      setModal(null);
      setErros({});
      await templates.recarregar();
    } catch (falha) {
      const campos = camposDoErro(falha);
      if (Object.keys(campos).length > 0) setErros(campos);
      notificar('erro', mensagemDoErro(falha));
    } finally {
      setSalvando(false);
    }
  };

  const remover = async () => {
    if (!removendo) return;
    setRemovendoAgora(true);
    try {
      await api.removerTemplate(removendo.id);
      notificar('sucesso', `Template "${removendo.nome}" removido.`);
      setRemovendo(null);
      await templates.recarregar();
    } catch (falha) {
      notificar('erro', mensagemDoErro(falha));
    } finally {
      setRemovendoAgora(false);
    }
  };

  const variaveisUsadas = variaveisDoCorpo(formulario.corpo);

  return (
    <>
      <TituloPagina
        titulo="Templates"
        descricao="Os textos que o Titular recebe. Variáveis entre chaves são preenchidas com os dados da fatura; qualquer outra é recusada antes do envio."
        acoes={
          <button type="button" className="botao botao--primario" onClick={abrirCriacao}>
            Novo template
          </button>
        }
      />

      <Aviso tipo="info" titulo="Template aprovado x texto livre">
        Se o Provedor já tem um template aprovado na Meta, informe o ID recebido pela XMAX. Sem
        isso, a mensagem sai como texto livre e só chega a quem falou com o número nas últimas 24h.
      </Aviso>

      {templates.carregando && !templates.dados ? (
        <Carregando texto="Carregando templates…" />
      ) : templates.erro && !templates.dados ? (
        <Aviso tipo="erro" titulo="Não foi possível carregar os templates">
          {templates.erro}
        </Aviso>
      ) : lista.length === 0 ? (
        <EstadoVazio
          titulo="Nenhum template ainda"
          texto="Crie o primeiro texto de cobrança. Você pode começar pelo modelo sugerido e ajustar as variáveis."
          icone="✎"
          acao={
            <button type="button" className="botao botao--primario" onClick={abrirCriacao}>
              Criar template
            </button>
          }
        />
      ) : (
        <div className="tabela-rolagem">
          <table className="tabela-templates">
            <caption>Mensagens disponíveis para as receitas deste Provedor.</caption>
            <thead>
              <tr>
                <th scope="col">Template</th>
                <th scope="col">Situação na Meta</th>
                <th scope="col">Atualizado</th>
                <th scope="col">Ações</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((template) => (
                <tr key={template.id}>
                  <td>
                    <strong>{template.nome}</strong>
                  </td>
                  <td>
                    {template.metaTemplateId ? (
                      <span className="badge badge--ok">
                        <span aria-hidden="true">✓</span> Aprovado #{template.metaTemplateId}
                      </span>
                    ) : (
                      <span className="badge badge--alerta">
                        <span aria-hidden="true">⚠</span> Texto livre
                      </span>
                    )}
                  </td>
                  <td className="numero">{formatarDataHora(template.atualizadoEm, fuso)}</td>
                  <td>
                    <div className="acoes-tabela">
                      <button
                        type="button"
                        className="botao botao--secundario botao--pequeno"
                        onClick={() => abrirEdicao(template)}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className="botao botao--perigo botao--pequeno"
                        onClick={() => setRemovendo(template)}
                      >
                        Remover
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        aberto={modal !== null}
        titulo={modal?.modo === 'editar' ? 'Editar template' : 'Novo template'}
        descricao="As variáveis disponíveis aparecem abaixo do texto. Clique para inserir na posição do cursor."
        onFechar={fecharModal}
        rodape={
          <>
            <button type="button" className="botao botao--secundario" onClick={fecharModal}>
              Cancelar
            </button>
            <button
              type="button"
              className="botao botao--primario"
              onClick={() => void salvar()}
              disabled={salvando}
            >
              {salvando ? 'Salvando…' : 'Salvar template'}
            </button>
          </>
        }
      >
        <div className="grade grade--2">
          <Campo rotulo="Nome" erro={erros.nome}>
            <input
              className="entrada"
              value={formulario.nome}
              onChange={(evento) =>
                setFormulario((atual) => ({ ...atual, nome: evento.target.value }))
              }
              placeholder="Cobrança padrão"
            />
          </Campo>
          <Campo
            rotulo="Chave"
            ajuda="Identificador estável, sem espaços."
            erro={erros.chave}
          >
            <input
              className="entrada mono"
              value={formulario.chave}
              disabled={modal?.modo === 'editar'}
              onChange={(evento) =>
                setFormulario((atual) => ({
                  ...atual,
                  chave: evento.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
                }))
              }
              placeholder="cobranca_padrao"
            />
          </Campo>
        </div>

        <Campo rotulo="Mensagem" erro={erros.corpo}>
          <textarea
            ref={areaCorpo}
            className="entrada"
            rows={7}
            value={formulario.corpo}
            onChange={(evento) =>
              setFormulario((atual) => ({ ...atual, corpo: evento.target.value }))
            }
          />
        </Campo>

        <div className="pilha pilha--apertada">
          <span className="campo__rotulo">Variáveis</span>
          <div className="linha">
            {VARIAVEIS.map((variavel) => (
              <button
                key={variavel.chave}
                type="button"
                className="botao botao--secundario botao--pequeno"
                title={variavel.descricao}
                onClick={() => inserirVariavel(variavel.chave)}
              >
                <span className="mono">{`{{${variavel.chave}}}`}</span>
              </button>
            ))}
          </div>
          <span className="texto-pequeno">
            Usadas neste texto:{' '}
            {variaveisUsadas.length === 0
              ? 'nenhuma'
              : variaveisUsadas.map((v) => `{{${v}}}`).join(', ')}
          </span>
        </div>

        <Campo
          rotulo="ID do template na Meta"
          opcional
          ajuda="Preenchido pela XMAX quando o template é aprovado. Deixe vazio para texto livre."
          erro={erros.metaTemplateId}
        >
          <input
            className="entrada"
            inputMode="numeric"
            value={formulario.metaTemplateId}
            onChange={(evento) =>
              setFormulario((atual) => ({
                ...atual,
                metaTemplateId: evento.target.value.replace(/\D/g, ''),
              }))
            }
            placeholder="1234567890"
          />
        </Campo>

        <aside className="previa" aria-label="Prévia do template">
          <span className="previa__rotulo">Prévia com dados de exemplo</span>
          <div className="previa__bolha">
            {preencherModelo(formulario.corpo, exemplo)}
            {'\n\n— '}
            {empresa}
            {'\nResponda SAIR para não receber mais.'}
          </div>
        </aside>
      </Modal>

      <Modal
        aberto={removendo !== null}
        titulo="Remover template"
        onFechar={() => (removendoAgora ? undefined : setRemovendo(null))}
        rodape={
          <>
            <button
              type="button"
              className="botao botao--secundario"
              onClick={() => setRemovendo(null)}
              disabled={removendoAgora}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="botao botao--perigo"
              onClick={() => void remover()}
              disabled={removendoAgora}
            >
              {removendoAgora ? 'Removendo…' : 'Remover template'}
            </button>
          </>
        }
      >
        <p>
          O template <strong>{removendo?.nome}</strong> deixa de existir. Receitas que o usam
          voltam a apontar para o texto padrão e podem parar de enviar até você escolher outro.
        </p>
        <Aviso tipo="alerta" titulo="Disparos já enviados não mudam">
          O histórico e a auditoria continuam intactos.
        </Aviso>
      </Modal>
    </>
  );
}
